import { AssetPath, AssetPathEncodeState, parseJson } from '@gnsx/genesys.js';


import { AbstractProvider } from './abstract-provider';
import {
  StorageProviderType
} from './interfaces';

import type {
  FileChangeAction,
  FileChangeEvent,
  FileChangeType,
  FileDownloadOptions,
  FileExistsOptions,
} from './interfaces';
import type { FileItem, FileListResult, FileUploadOptions } from '@gnsx/genesys.js';

import { isSdkWindow } from '@/app/sdk';
import { DEFAULT_FILE_SERVER_URL, ENGINE_VERSION } from '@/const';

const DEBUG_LOG = {
  fileChanged: false,
  uploadFile: false,
  downloadFile: false,
  deleteItem: true,
  createDirectory: true,
} as const;

// Interface to match the server response structure
interface ServerFileItem {
  name: string;
  path: string;
  absolutePath: string;
  size: number;
  modifiedTime: string;
  contentType?: string;
}

interface ServerDirectoryItem {
  name: string;
  path: string;
  absolutePath: string;
  size: number;
  modifiedTime: string;
}

interface ServerListResponse {
  files: ServerFileItem[];
  directories: ServerDirectoryItem[];
}

interface FileExistsResponse {
  exists: boolean;
}

export async function syncFileServerProviderUrlFromSdk(provider: FileServerProvider): Promise<string | null> {
  if (!isSdkWindow() || typeof window === 'undefined') {
    return null;
  }
  const status = await window.electronAPI?.fileServer.status();
  const baseUrl = status?.baseUrl;
  if (baseUrl) {
    provider.setServerUrl(baseUrl);
    return baseUrl;
  }
  return null;
}

/**
 * File Server provider implementation
 * Implements the IStorageProvider interface for the local file server
 */
export class FileServerProvider extends AbstractProvider {
  private serverUrl: string;
  private ws: WebSocket | null = null;
  private fileChangeCallbacks: Map<symbol, (event: FileChangeEvent) => void> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private shouldWatchFileChanges = false;

  private getWebSocketUrl(): string {
    const url = new URL(this.serverUrl);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return url.toString();
  }

  constructor() {
    super();
    this.serverUrl = DEFAULT_FILE_SERVER_URL;
  }

  get providerType(): StorageProviderType {
    return StorageProviderType.FILE_SERVER;
  }

  /**
   * Get the file server URL
   */
  getServerUrl(): string {
    return this.serverUrl;
  }

  /**
   * Set the file server URL
   */
  setServerUrl(url: string): void {
    this.serverUrl = url;
  }

  requireProjectContext(): boolean {
    return false;
  }

  /**
   * Lists files and directories at the specified path
   */
  async doListFiles(path: AssetPath, recursive: boolean): Promise<FileListResult> {
    try {
      const queryParams = new URLSearchParams();
      // NOTE: DO NOT use resolved path here
      queryParams.append('path', path.initialPath);

      if (recursive) {
        queryParams.append('recursive', 'true');
      }

      const url = `${this.serverUrl}/api/files?${queryParams.toString()}`;
      const response = await fetch(url);
      if (DEBUG_LOG.fileChanged) console.log('Listing files at', url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to list files' }));
        throw new Error(errorData.error ?? `FileServerProvider: Failed to list files: ${response.status} ${response.statusText}`);
      }

      const data = await response.json() as ServerListResponse;

      // Convert the server response to match our interface
      const files: FileItem[] = data.files.map((file: ServerFileItem) => ({
        name: file.name,
        path: file.path,
        absolutePath: file.absolutePath,
        size: file.size,
        modifiedTime: new Date(file.modifiedTime),
        contentType: file.contentType,
        isDirectory: false
      }));

      const directories: FileItem[] = data.directories.map((dir: ServerDirectoryItem) => ({
        name: dir.name,
        path: dir.path,
        absolutePath: dir.absolutePath,
        size: dir.size,
        modifiedTime: new Date(dir.modifiedTime),
        isDirectory: true
      }));

      return {
        files,
        directories
      };
    } catch (error) {
      console.warn(`Error listing files at ${path}:`, error);
      throw new Error(`FileServerProvider: Failed to list files: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Uploads a file to the specified path
   */
  async uploadFile(
    path: AssetPath,
    content: Blob | File | string | ArrayBuffer,
    options?: FileUploadOptions
  ): Promise<{ path: string; name: string }> {
    try {
      const formData = new FormData();
      let filename = path.initialPath.split('/').pop() ?? '';

      // Extract the directory path and filename from the full path
      const directoryPath = path.initialPath.substring(0, path.initialPath.lastIndexOf('/'));

      // Convert content to a Blob if it's not already
      let blob: Blob;
      if (content instanceof Blob || content instanceof File) {
        blob = content;
      } else if (typeof content === 'string') {
        blob = new Blob([content], { type: options?.contentType ?? 'text/plain' });
      } else if (content instanceof ArrayBuffer) {
        blob = new Blob([content], { type: options?.contentType ?? 'application/octet-stream' });
      } else {
        throw new Error('Unsupported content type');
      }

      // If content is already a File object, keep the filename
      if (content instanceof File) {
        filename = content.name;
      }

      // Create a File object with the correct name
      const file = new File([blob], filename, { type: blob.type });

      formData.append('file', file);

      // Use XMLHttpRequest for progress tracking if onProgress callback is provided
      if (options?.onProgress) {
        return new Promise<{ path: string; name: string }>((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && options.onProgress) {
              const progress = Math.round((event.loaded / event.total) * 100);
              options.onProgress(progress);
            }
          };

          xhr.onload = async () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const result = parseJson(xhr.responseText);
                resolve({
                  path: result.path,
                  name: result.filename
                });
              } catch (error) {
                reject(new Error('Failed to parse upload response'));
              }
            } else {
              try {
                const errorData = parseJson(xhr.responseText);
                reject(new Error(errorData.error ?? `Failed to upload file: ${xhr.status} ${xhr.statusText}`));
              } catch {
                reject(new Error(`Failed to upload file: ${xhr.status} ${xhr.statusText}`));
              }
            }
          };

          xhr.onerror = () => {
            reject(new Error('Network error occurred during upload'));
          };

          xhr.open('POST', `${this.serverUrl}/api/files/upload`);
          xhr.setRequestHeader('x-upload-path', directoryPath);
          xhr.send(formData);
        });
      }

      // Fallback to fetch for uploads without progress tracking
      const response = await fetch(`${this.serverUrl}/api/files/upload`, {
        method: 'POST',
        body: formData,
        headers: {
          'x-upload-path': directoryPath
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to upload file' }));
        throw new Error(errorData.error ?? `Failed to upload file: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      return {
        path: result.path,
        name: result.filename
      };
    } catch (error) {
      console.error(`Error uploading file to ${path}:`, error);
      throw new Error(`Failed to upload file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Downloads a file from the specified path
   */
  async downloadFile(
    path: AssetPath,
    options?: FileDownloadOptions
  ): Promise<Blob | string | ArrayBuffer> {
    try {
      path = await this.resolvePath(path);
      if (DEBUG_LOG.downloadFile) console.log(`Downloading file from ${path}`);
      const response = await fetch(path.getResolvedPath());

      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
      }

      // If explicitly requesting a blob, return it
      if (options?.asBlob) {
        return await response.blob();
      }

      const textContent = await this.extractTextContent(response, path.initialPath);
      if (textContent !== null) {
        return textContent;
      }

      // For all other cases, get as ArrayBuffer
      return await response.arrayBuffer();
    } catch (error) {
      console.error(`Error downloading file from ${path}:`, error);
      throw new Error(`Failed to download file: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Deletes a file or directory
   */
  async deleteItem(path: AssetPath, recursive = true): Promise<void> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('path', path.initialPath);

      const response = await fetch(`${this.serverUrl}/api/files?${queryParams.toString()}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to delete item' }));
        throw new Error(errorData.error ?? `Failed to delete item: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Error deleting item at ${path}:`, error);
      throw new Error(`Failed to delete item: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Creates a directory at the specified path
   */
  async createDirectory(path: AssetPath): Promise<void> {
    try {
      // The file server creates directories by creating a placeholder file
      const placeholderPath = AssetPath.join(path.initialPath, '.placeholder');
      const response = await fetch(`${this.serverUrl}/api/files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          path: placeholderPath,
          content: ''
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create directory' }));
        throw new Error(errorData.error ?? `Failed to create directory: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Error creating directory at ${path}:`, error);
      throw new Error(`Failed to create directory: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Checks if a file or directory exists
   * Note: This implementation fetches the file to check if it exists
   */
  async exists(path: AssetPath, options?: FileExistsOptions): Promise<boolean> {
    try {
      // For directories, try to list contents
      if (path.initialPath.endsWith('/')) {
        await this.listFiles(path, false, false);
        return true;
      }

      if (options?.silent) {
        const queryParams = new URLSearchParams({ path: path.initialPath });
        const response = await fetch(`${this.serverUrl}/api/files/exists?${queryParams.toString()}`);

        if (!response.ok) {
          return false;
        }

        const result = await response.json() as FileExistsResponse;
        return result.exists;
      }

      // For files, try to fetch the file
      path = await this.resolvePath(path);
      const response = await fetch(path.getResolvedPath(), {
        method: 'HEAD'
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Gets URL for a file
   */
  async resolvePath(path: AssetPath): Promise<AssetPath> {
    if (path.isResolved()) {
      return path;
    }
    path.resolvePath(AssetPath.join(this.serverUrl, path.initialPath), AssetPathEncodeState.Unknown);
    return path;
  }

  public override async buildCurrentProject(): Promise<boolean> {
    try {
      const response = await fetch(`${this.serverUrl}/api/build-project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          engineVersion: ENGINE_VERSION,
        })
      });
      return response.ok;
    } catch (error) {
      console.error('Error building project', error);
      return false;
    }
  }

  protected override async exec(command: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.serverUrl}/api/exec`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          command
        })
      });
      return response.ok;
    } catch (error) {
      console.error(`Error executing command: ${command}`, error);
      return false;
    }
  }

  /**
   * Clean up resources when the provider is no longer needed
   */
  dispose() {
    this.shouldWatchFileChanges = false;
    this.disconnect();
    this.fileChangeCallbacks.clear();
  }

  /**
   * Handle file system changes
   * @returns A function to unsubscribe from file system changes
   */
  /** Clears reconnect backoff so a fresh subscription can connect after project reopen. */
  resetReconnectState(): void {
    this.reconnectAttempts = 0;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  onFileSystemChange(callback: (event: FileChangeEvent) => void): () => void {
    const id = Symbol();
    this.fileChangeCallbacks.set(id, callback);
    this.shouldWatchFileChanges = true;
    this.resetReconnectState();
    void this.connect();

    // Return unsubscribe function
    return () => {
      this.fileChangeCallbacks.delete(id);
      if (this.fileChangeCallbacks.size === 0) {
        this.shouldWatchFileChanges = false;
        this.reconnectAttempts = 0;
        this.disconnect();
      }
    };
  }

  private async connect() {
    if (!this.shouldWatchFileChanges || this.fileChangeCallbacks.size === 0) {
      return;
    }

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const canConnect = await this.refreshConnectionStateFromSdk();
    if (!canConnect) {
      this.attemptReconnect();
      return;
    }

    try {
      const wsUrl = this.getWebSocketUrl();
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        if (DEBUG_LOG.fileChanged) console.log('WebSocket connected for file system monitoring');
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const data = parseJson(event.data);
          if (DEBUG_LOG.fileChanged) console.log('File system change:', data);
          this.fileChangeCallbacks.forEach(callback => callback({
            type: data.type as FileChangeType,
            path: data.path,
            action: data.action as FileChangeAction
          }));
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = () => {
        this.ws = null;
        if (DEBUG_LOG.fileChanged) console.log('WebSocket disconnected');
        if (this.shouldWatchFileChanges && this.fileChangeCallbacks.size > 0) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.warn('WebSocket error:', error);
      };
    } catch (error) {
      this.ws = null;
      console.warn('Error connecting to WebSocket:', error);
      this.attemptReconnect();
    }
  }

  private attemptReconnect() {
    if (!this.shouldWatchFileChanges || this.fileChangeCallbacks.size === 0) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      void this.connect();
    }, Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000));
  }

  private async refreshConnectionStateFromSdk(): Promise<boolean> {
    if (!isSdkWindow() || typeof window === 'undefined') {
      return true;
    }

    try {
      const status = await window.electronAPI?.fileServer.status();
      if (!status?.isRunning) {
        return false;
      }
      if (status.baseUrl) {
        this.serverUrl = status.baseUrl;
      }
      return true;
    } catch (error) {
      console.warn('[FileServerProvider] Failed to query file server status before WebSocket connect:', error);
      return true;
    }
  }

  private disconnect() {
    if (this.ws) {
      // Prevent intentional disconnect from triggering reconnect logic.
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
}
